import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Banknote, AlertTriangle, Settings2, FileDown, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const API = '';
const STAGES = [
  { key: 'cutting', name: 'Cutting' },
  { key: 'prep_printing', name: 'Printing' },
  { key: 'prep_skiving', name: 'Skiving' },
  { key: 'prep_marking', name: 'Marking' },
  { key: 'prep_crimping', name: 'Crimping' },
  { key: 'prep_nailing', name: 'Nailing' },
  { key: 'prep_socks', name: 'Socks' },
  { key: 'closing', name: 'Closing' },
  { key: 'lasting', name: 'Lasting' },
  { key: 'pasting', name: 'Pasting' },
  { key: 'finishing', name: 'Finishing' },
  { key: 'packing', name: 'Packing' },
  { key: 'dispatch', name: 'Dispatch' },
];

export default function Wages() {
  const [wagesData, setWagesData] = useState({ detail: [], worker_totals: [] });
  const [stageConfigs, setStageConfigs] = useState([]);
  const [filterWorker, setFilterWorker] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [workers, setWorkers] = useState([]);
  const [configModal, setConfigModal] = useState(false);
  const [editStage, setEditStage] = useState(null);
  const [configForm, setConfigForm] = useState({ loss_threshold_percent: '', penalty_type: 'rate_based', penalty_rate_per_unit: '', penalty_fixed_amount: '' });
  const [loading, setLoading] = useState(false);

  // Weekly report state
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');
  const [reportWorker, setReportWorker] = useState('');
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const fetchWages = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterWorker) params.append('worker_id', filterWorker);
      if (filterStage) params.append('stage', filterStage);
      const { data } = await axios.get(`${API}/api/wages/summary?${params}`, { withCredentials: true });
      setWagesData(data);
    } catch {}
  }, [filterWorker, filterStage]);

  const fetchConfigs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/stage-config`, { withCredentials: true });
      setStageConfigs(data);
    } catch {}
  }, []);

  const fetchWorkers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/job-workers`, { withCredentials: true });
      setWorkers(data);
    } catch {}
  }, []);

  useEffect(() => { fetchWages(); fetchConfigs(); fetchWorkers(); }, [fetchWages, fetchConfigs, fetchWorkers]);

  // Set default date range to current week
  useEffect(() => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const monday = new Date(now);
    monday.setDate(now.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    setReportFrom(monday.toISOString().split('T')[0]);
    setReportTo(sunday.toISOString().split('T')[0]);
  }, []);

  const openConfig = (cfg) => {
    setEditStage(cfg.stage);
    setConfigForm({
      loss_threshold_percent: cfg.loss_threshold_percent || '',
      penalty_type: cfg.penalty_type || 'rate_based',
      penalty_rate_per_unit: cfg.penalty_rate_per_unit || '',
      penalty_fixed_amount: cfg.penalty_fixed_amount || '',
    });
    setConfigModal(true);
  };

  const saveConfig = async () => {
    setLoading(true);
    try {
      const payload = {};
      if (configForm.loss_threshold_percent !== '') payload.loss_threshold_percent = parseFloat(configForm.loss_threshold_percent);
      payload.penalty_type = configForm.penalty_type;
      if (configForm.penalty_rate_per_unit !== '') payload.penalty_rate_per_unit = parseFloat(configForm.penalty_rate_per_unit);
      if (configForm.penalty_fixed_amount !== '') payload.penalty_fixed_amount = parseFloat(configForm.penalty_fixed_amount);
      await axios.put(`${API}/api/stage-config/${editStage}`, payload, { withCredentials: true });
      setConfigModal(false);
      fetchConfigs();
    } catch {}
    finally { setLoading(false); }
  };

  const fetchWeeklyReport = async () => {
    if (!reportFrom || !reportTo) { toast.error('Select date range'); return; }
    setReportLoading(true);
    try {
      const params = new URLSearchParams({ date_from: reportFrom, date_to: reportTo });
      if (reportWorker) params.append('worker_id', reportWorker);
      const { data } = await axios.get(`${API}/api/wages/weekly-report?${params}`, { withCredentials: true });
      setWeeklyReport(data);
    } catch { toast.error('Failed to fetch report'); }
    finally { setReportLoading(false); }
  };

  const downloadWeeklyReport = async () => {
    if (!reportFrom || !reportTo) { toast.error('Select date range'); return; }
    try {
      const params = new URLSearchParams({ date_from: reportFrom, date_to: reportTo });
      if (reportWorker) params.append('worker_id', reportWorker);
      const response = await axios.get(`${API}/api/wages/weekly-report/download?${params}`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `wages_report_${reportFrom}_to_${reportTo}.xlsx`);
      document.body.appendChild(link);
      link.click(); link.remove();
      toast.success('Report downloaded');
    } catch { toast.error('Failed to download report'); }
  };

  return (
    <div className="p-6" data-testid="wages-page">
      <div className="mb-6">
        <h1 className="font-heading font-black text-3xl tracking-tighter text-[#09090B]">Wages & Penalties</h1>
        <p className="text-sm text-zinc-500">Worker wages, weekly reports, loss penalties, and stage penalty configuration</p>
      </div>

      {/* Weekly Wages Report */}
      <div className="bg-white border border-zinc-200 mb-6">
        <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
          <Calendar size={18} className="text-zinc-500" />
          <h2 className="font-heading font-bold text-lg">Weekly Wages Report</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">From</Label>
              <Input type="date" value={reportFrom} onChange={e => setReportFrom(e.target.value)} className="mt-1 h-9 rounded-sm text-sm" data-testid="report-from" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">To</Label>
              <Input type="date" value={reportTo} onChange={e => setReportTo(e.target.value)} className="mt-1 h-9 rounded-sm text-sm" data-testid="report-to" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Worker</Label>
              <Select value={reportWorker || 'all'} onValueChange={v => setReportWorker(v === 'all' ? '' : v)}>
                <SelectTrigger className="mt-1 h-9 rounded-sm text-sm" data-testid="report-worker"><SelectValue placeholder="All Workers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Workers</SelectItem>
                  {workers.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button className="h-9 rounded-sm bg-[#09090B]" onClick={fetchWeeklyReport} disabled={reportLoading} data-testid="generate-report">
                {reportLoading ? 'Loading...' : 'Generate'}
              </Button>
              <Button variant="outline" className="h-9 rounded-sm" onClick={downloadWeeklyReport} data-testid="download-report">
                <FileDown size={14} className="mr-1" /> Excel
              </Button>
            </div>
          </div>

          {weeklyReport && (
            <div>
              {/* Grand Totals */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div className="bg-zinc-50 p-3 rounded-sm">
                  <div className="text-[10px] uppercase text-zinc-500">Total Wages</div>
                  <div className="font-mono font-bold text-lg">{(weeklyReport.grand_total_wages || 0).toLocaleString()}</div>
                </div>
                <div className="bg-red-50 p-3 rounded-sm">
                  <div className="text-[10px] uppercase text-red-500">Total Penalty</div>
                  <div className="font-mono font-bold text-lg text-red-600">{(weeklyReport.grand_total_penalty || 0).toLocaleString()}</div>
                </div>
                <div className="bg-emerald-50 p-3 rounded-sm">
                  <div className="text-[10px] uppercase text-emerald-500">Net Payable</div>
                  <div className="font-mono font-bold text-lg text-emerald-700">{(weeklyReport.grand_net_wages || 0).toLocaleString()}</div>
                </div>
              </div>

              {/* Per Worker Detail */}
              {weeklyReport.workers?.map(w => (
                <div key={w.worker_id} className="border border-zinc-200 rounded-sm mb-3" data-testid={`report-worker-${w.worker_id}`}>
                  <div className="p-3 bg-zinc-50 flex items-center justify-between">
                    <div>
                      <span className="font-bold text-sm">{w.worker_name}</span>
                      <span className="text-xs text-zinc-500 ml-3">Entries: {w.entries?.length || 0}</span>
                    </div>
                    <div className="flex gap-4 text-xs font-mono">
                      <span>Wages: <strong>{w.total_wages}</strong></span>
                      <span className="text-red-600">Penalty: <strong>{w.total_penalty}</strong></span>
                      <span className="text-emerald-700">Net: <strong>{w.net_wages}</strong></span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-zinc-50/50">
                        <tr>
                          <th className="text-left p-2 text-[10px] uppercase text-zinc-500">Date</th>
                          <th className="text-left p-2 text-[10px] uppercase text-zinc-500">Job Card</th>
                          <th className="text-left p-2 text-[10px] uppercase text-zinc-500">SKU</th>
                          <th className="text-left p-2 text-[10px] uppercase text-zinc-500">Stage</th>
                          <th className="text-right p-2 text-[10px] uppercase text-zinc-500">In</th>
                          <th className="text-right p-2 text-[10px] uppercase text-zinc-500">Out</th>
                          <th className="text-right p-2 text-[10px] uppercase text-zinc-500">Loss</th>
                          <th className="text-right p-2 text-[10px] uppercase text-zinc-500">Rate</th>
                          <th className="text-right p-2 text-[10px] uppercase text-zinc-500">Wages</th>
                          <th className="text-right p-2 text-[10px] uppercase text-zinc-500">Penalty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(w.entries || []).map((e, i) => (
                          <tr key={i} className="border-t border-zinc-100">
                            <td className="p-2">{e.date}</td>
                            <td className="p-2 font-mono font-bold">{e.job_card_number}</td>
                            <td className="p-2 font-mono">{e.sku}/{e.size}</td>
                            <td className="p-2">{e.stage_name}</td>
                            <td className="p-2 text-right font-mono">{e.input_qty}</td>
                            <td className="p-2 text-right font-mono">{e.output_qty}</td>
                            <td className="p-2 text-right font-mono text-red-600">{e.loss_qty > 0 ? `-${e.loss_qty}` : '0'}</td>
                            <td className="p-2 text-right font-mono">{e.rate_per_pair}</td>
                            <td className="p-2 text-right font-mono">{e.wages}</td>
                            <td className="p-2 text-right font-mono text-red-600">{e.penalty_amount > 0 ? e.penalty_amount : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              {weeklyReport.workers?.length === 0 && (
                <div className="p-6 text-center text-zinc-500">No production entries found for this period.</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stage Penalty Configuration */}
      <div className="bg-white border border-zinc-200 mb-6">
        <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
          <Settings2 size={18} className="text-zinc-500" />
          <h2 className="font-heading font-bold text-lg">Stage Penalty Configuration</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="stage-config-table">
            <thead className="bg-zinc-50">
              <tr>
                <th className="text-left p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Stage</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Loss Threshold %</th>
                <th className="text-left p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Penalty Type</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Penalty Rate/Unit</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Fixed Amount</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {stageConfigs.map((cfg, idx) => (
                <tr key={cfg.stage} className={`border-t border-zinc-200 ${idx % 2 === 1 ? 'bg-zinc-50/30' : ''}`}>
                  <td className="p-3 font-medium">{cfg.display_name}</td>
                  <td className="p-3 text-right font-mono text-xs">{cfg.loss_threshold_percent}%</td>
                  <td className="p-3 text-xs capitalize">{cfg.penalty_type?.replace('_', ' ')}</td>
                  <td className="p-3 text-right font-mono text-xs">{cfg.penalty_rate_per_unit}</td>
                  <td className="p-3 text-right font-mono text-xs">{cfg.penalty_fixed_amount}</td>
                  <td className="p-3 text-right">
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => openConfig(cfg)} data-testid={`edit-config-${cfg.stage}`}>Edit</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Wages Summary */}
      <div className="bg-white border border-zinc-200 mb-6">
        <div className="p-4 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Banknote size={18} className="text-zinc-500" />
            <h2 className="font-heading font-bold text-lg">Worker Wages Summary (All Time)</h2>
          </div>
          <div className="flex gap-2">
            <Select value={filterWorker || 'all'} onValueChange={v => setFilterWorker(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 rounded-sm text-xs w-40"><SelectValue placeholder="All Workers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Workers</SelectItem>
                {workers.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStage || 'all'} onValueChange={v => setFilterStage(v === 'all' ? '' : v)}>
              <SelectTrigger className="h-8 rounded-sm text-xs w-36"><SelectValue placeholder="All Stages" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="wages-table">
            <thead className="bg-zinc-50">
              <tr>
                <th className="text-left p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Worker</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Total Input</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Total Output</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Total Loss</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Loss %</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Gross Wages</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Penalty</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Net Wages</th>
              </tr>
            </thead>
            <tbody>
              {wagesData.worker_totals.map((wt, idx) => (
                <tr key={wt.worker_id} className={`border-t border-zinc-200 ${idx % 2 === 1 ? 'bg-zinc-50/30' : ''}`} data-testid={`wages-row-${wt.worker_id}`}>
                  <td className="p-3 font-medium">{wt.worker_name}</td>
                  <td className="p-3 text-right font-mono text-xs">{(wt.total_input || 0).toLocaleString()}</td>
                  <td className="p-3 text-right font-mono text-xs">{(wt.total_output || 0).toLocaleString()}</td>
                  <td className="p-3 text-right font-mono text-xs">{wt.total_loss || 0}</td>
                  <td className="p-3 text-right">
                    <span className={`font-mono text-xs font-bold ${(wt.loss_percent || 0) > 2 ? 'text-red-600' : 'text-green-600'}`}>{wt.loss_percent || 0}%</span>
                  </td>
                  <td className="p-3 text-right font-mono text-xs">{(wt.total_wages || 0).toLocaleString()}</td>
                  <td className="p-3 text-right">
                    {(wt.total_penalty || 0) > 0 ? (
                      <span className="font-mono text-xs font-bold text-red-600 flex items-center justify-end gap-1">
                        <AlertTriangle size={10} /> -{(wt.total_penalty || 0).toLocaleString()}
                      </span>
                    ) : <span className="font-mono text-xs text-zinc-400">0</span>}
                  </td>
                  <td className="p-3 text-right font-mono text-xs font-bold text-[#09090B]">{(wt.net_wages || 0).toLocaleString()}</td>
                </tr>
              ))}
              {wagesData.worker_totals.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-zinc-500">No wage data yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Config Edit Modal */}
      {configModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setConfigModal(false)}>
          <div className="bg-white rounded-sm border border-zinc-200 p-6 max-w-sm w-full" onClick={e => e.stopPropagation()} data-testid="config-edit-modal">
            <h3 className="font-bold text-lg mb-4">Edit Penalty Config — {STAGES.find(s => s.key === editStage)?.name}</h3>
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Loss Threshold %</Label>
                <Input type="number" step="0.1" value={configForm.loss_threshold_percent} onChange={e => setConfigForm(p => ({ ...p, loss_threshold_percent: e.target.value }))} className="mt-1 h-8 rounded-sm text-sm" data-testid="config-threshold" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Penalty Type</Label>
                <Select value={configForm.penalty_type} onValueChange={v => setConfigForm(p => ({ ...p, penalty_type: v }))}>
                  <SelectTrigger className="mt-1 h-8 rounded-sm text-sm" data-testid="config-penalty-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rate_based">Rate Based (per excess unit)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount (per excess unit)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {configForm.penalty_type === 'rate_based' ? (
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Penalty Rate per Unit</Label>
                  <Input type="number" step="0.01" value={configForm.penalty_rate_per_unit} onChange={e => setConfigForm(p => ({ ...p, penalty_rate_per_unit: e.target.value }))} className="mt-1 h-8 rounded-sm text-sm" data-testid="config-penalty-rate" />
                </div>
              ) : (
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Fixed Penalty Amount</Label>
                  <Input type="number" step="0.01" value={configForm.penalty_fixed_amount} onChange={e => setConfigForm(p => ({ ...p, penalty_fixed_amount: e.target.value }))} className="mt-1 h-8 rounded-sm text-sm" data-testid="config-penalty-fixed" />
                </div>
              )}
              <div className="flex justify-end gap-3 pt-3 border-t">
                <Button variant="outline" className="h-9 rounded-sm" onClick={() => setConfigModal(false)}>Cancel</Button>
                <Button className="h-9 rounded-sm bg-[#09090B]" onClick={saveConfig} disabled={loading} data-testid="save-config">{loading ? 'Saving...' : 'Save'}</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
